import { useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { Logger } from '../support/Logger';
import { AuthorizedMediaUrlResolver } from './AuthorizedMediaUrlResolver';

export type IResolvedMedia =
    | { status: 'none' }
    | { status: 'resolving' }
    | { status: 'resolved'; url: string }
    | { status: 'unavailable' };

// The url a media element should actually load, and whether it can be loaded at all. Every surface
// that renders an attachment or a member photo goes through this, so these decisions are made once
// rather than in each component.
//
// Nothing is offered until the authorised url is ready, when one is coming. Seeding with the stored
// url instead would fetch the public bucket on every paint: unaudited, and a 403 once it is closed.
//
// The result is held against the url and href it was computed for, and anything else reads as
// still resolving. Components here are reused across people — one avatar instance serves the care
// recipient switcher — and effects run after paint, so a bare result would show the outgoing
// person's face under the incoming person's name until the reset landed.
//
// 'unavailable' is distinct from 'resolving' because callers must not treat them alike: showing the
// failed state while a token fetch is in flight puts a silhouette where a face is about to be.
export const useResolvedMedia = (params: { url?: string; href?: string }): IResolvedMedia => {
    const { url, href } = params;
    const resolver = useInstance(AuthorizedMediaUrlResolver);
    const log = useInstance(Logger);

    const key = `${url ?? ''}|${href ?? ''}`;
    const [result, setResult] = useState<{ key: string; media: IResolvedMedia }>();
    const latestKey = useRef<string>('');

    useAsyncEffect(async () => {
        latestKey.current = key;

        if (!url || !href) {
            return;
        }

        // Caught rather than propagated. useAsyncEffect rethrows into the nearest error boundary,
        // and avatars are scattered across a care map, so one failed mint would take down the whole
        // screen. The resolver refuses to authorise a read it cannot account for, and that must
        // degrade to the caller's failure state, never to an unaudited fetch of the public bucket.
        // Logged so a persistent failure is visible rather than merely quiet.
        try {
            const resolved = await resolver.resolve({ url, href });
            if (latestKey.current === key) {
                setResult({ key, media: { status: 'resolved', url: resolved } });
            }
        } catch (e) {
            log.error(e, { context: 'could not authorise a media read', href });
            if (latestKey.current === key) {
                setResult({ key, media: { status: 'unavailable' } });
            }
        }
    }, [url, href]);

    if (!url) {
        return { status: 'none' };
    }
    // No link means the server has no audited route for this object (a social login's picture, or a
    // local preview), so the stored url is the one to load and there is nothing to wait for.
    if (!href) {
        return { status: 'resolved', url };
    }
    return result && result.key === key ? result.media : { status: 'resolving' };
};
